import { useWrapAsync } from "@paroicms/front-app-log";
import { useEffect, useState } from "react";
import { appLog } from "../../../../../context";
import { checkIsRequestError } from "../../../../../helpers/check-schema-field-type";
import { getSeq } from "../../../../../helpers/get-seq";
import OurDropdown from "../../../utils/OurDropdown/OurDropdown";
import DefineKpiContent from "./DefineKpiContent/DefineKpiContent";

import { fetchStatisticsByReference } from "../../../../../api/application/statistics";
import { fetchTables } from "../../../../../api/application/table";
import type { GeneratedReference } from "../../../../../api/infrastructure/reference.formatter";
import type { RequestError } from "../../../../../api/infrastructure/statistics.api";
import type { Statistics } from "../../../../../api/infrastructure/statistics.formatter";
import type { TableForm } from "../../../../../api/infrastructure/table-appendix.formatter";
import type { APITable } from "../../../../../api/infrastructure/table.formatter";
import { apiRequestWrapper } from "../../../../../api/shared/http-client";
import useGetTableForm from "../../../../hooks/useGetTableForm";
import "./DefineKpi.scss";

export interface KpiInputExtended {
  error?: string;
  id: string;
  name: string | null;
  property: string;
  value: string | boolean | null;
}

export default function DefineKpi({
  className,
  generatedReferences,
}: { className?: string; generatedReferences: GeneratedReference[] }) {
  const wrapAsync = useWrapAsync();
  const [selectedReferenceId, setSelectedReferenceId] = useState<number>();
  const [selectedTableId, setSelectedTableId] = useState<string>();
  const [selectedReference, setSelectedReference] = useState<GeneratedReference>();
  const [kpiInputValuesExtended, setKpiInputValuesExtended] = useState<KpiInputExtended[]>();
  const [kpiColumns, setKpiColumns] = useState<TableForm[]>();
  const [statistics, setStatistics] = useState<Statistics | RequestError>();
  const [tables, setTables] = useState<APITable[]>();
  const { tableForm, fetchTableForm } = useGetTableForm();

  const onSelectedReferenceChange = (value: number) => {
    setSelectedReferenceId(value);
    setSelectedTableId(undefined);
  };
  const onSelectedTableChange = (value: string) => {
    setSelectedTableId(value);
    setKpiInputValuesExtended(undefined);
    setKpiColumns(undefined);
    setStatistics(undefined);
  };
  const refreshKpiInputValues = () => {
    if (selectedReferenceId === undefined || statistics === undefined) return;
    const foundReference = generatedReferences.find((r) => r.id === selectedReferenceId);
    if (!foundReference) return;
    const validSchemaFields = tableForm;
    // TODO: handle all schema types
    // const validSchemaFields = tableForm.filter((f) => f.type !== "file");
    if (validSchemaFields.length === 0) return;
    setSelectedReference(foundReference);
    setKpiColumns([...validSchemaFields]);
    let kpis: KpiInputExtended[] = [];
    if (!checkIsRequestError(statistics) && statistics.kpis.length > 0) {
      for (const k of statistics.kpis.slice(0, 4)) {
        if (k.property) {
          kpis.push({
            id: `${k.property}${getSeq()}`,
            name: k.name ?? null,
            property: k.property,
            value: k.value ?? null,
          });
        }
      }
      const remains: TableForm[] = [];
      for (const r of validSchemaFields) {
        const found = kpis.find((v) => v.property === r.name);
        if (!found) remains.push(r);
      }
      if (remains.length > 0) {
        kpis.push(...addKpis(remains.length, remains));
      }
    } else {
      kpis = addKpis(validSchemaFields.length, validSchemaFields);
    }
    setKpiInputValuesExtended([...kpis]);
  };

  useEffect(
    wrapAsync(async () => {
      if (selectedTableId === undefined) return;
      try {
        setStatistics(await fetchStatisticsByReference(Number(selectedTableId)));
      } catch (error) {
        const message = error instanceof Error ? error.message : "une erreur est survenue";
        appLog.error(error);
        setStatistics({
          error: message,
        });
      }
    }),
    [selectedTableId],
  );
  useEffect(refreshKpiInputValues, [selectedTableId, statistics]);
  useEffect(
    wrapAsync(async () => {
      if (!selectedReferenceId) return;
      await apiRequestWrapper(async () => {
        const result = await fetchTables(selectedReferenceId);
        setTables(result);
      });
    }),
    [selectedReferenceId],
  );
  useEffect(() => {
    if (!selectedReferenceId || !selectedTableId) return;
    fetchTableForm({ referenceId: selectedReferenceId, tableId: Number(selectedTableId) });
  }, [selectedTableId]);

  return (
    <div className={`Card DefineKpi${className ? ` ${className}` : ""}`}>
      <h2 className="DefineKpi-title">Définir les KPI</h2>

      <div className="DefineKpi-selectionFields flex flex-col md:flex-row gap-4">
        <label className="Field w-full">
          <span className="Field-label">Sélectionner un référentiel</span>
          <OurDropdown<number>
            selectedOption={selectedReferenceId}
            initialOptions={generatedReferences.map((d) => ({
              label: d.name,
              value: d.id,
            }))}
            onChange={onSelectedReferenceChange}
            filter
            leftIcon
          />
        </label>

        <label className="Field w-full">
          <span className="Field-label">Sélectionner un table</span>
          <OurDropdown<string>
            selectedOption={selectedTableId}
            initialOptions={tables?.map((d) => ({
              label: d.name,
              value: d.id,
            }))}
            onChange={(value) => onSelectedTableChange(value)}
            filter
            leftIcon
          />
        </label>
      </div>

      {selectedTableId && (
        <DefineKpiContent
          tableForm={tableForm}
          selectedReference={selectedReference}
          selectedTableId={Number(selectedTableId)}
          kpiInputValuesExtended={kpiInputValuesExtended}
          kpiColumns={kpiColumns}
          refreshKpiInputValues={refreshKpiInputValues}
          onKpiInputValuesChange={(kpisInputValues) =>
            setKpiInputValuesExtended([...kpisInputValues])
          }
          onSuccess={() => setStatistics(undefined)}
        />
      )}
    </div>
  );
}

function addKpis(remains: number, validSchemaFields: TableForm[]): KpiInputExtended[] {
  // This is to prevent infinite loop in while
  if (validSchemaFields.length === 0) {
    throw new Error("empty valid schema fields");
  }
  let n = remains;
  const kpis: KpiInputExtended[] = [];
  while (n > 0) {
    for (const vField of validSchemaFields) {
      if (n === 0) break;
      kpis.push({
        name: vField.label,
        property: vField.name,
        value: " ", // default value as space to avoid empty value
        id: `${vField.name}${getSeq()}`,
      });
      --n;
    }
  }
  return kpis;
}
